import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { adminPaymentService, PlanDetailsResponse, FreeUpgradeSetting, AddTierRequest } from '@/services/adminPaymentService';
import AddTierModal from './AddTierModal';
import { toastHelper } from '@/utils/toastHelper';
import { calculatePromotionDiscount, calculateAnnualSavings, calculateCombinedDiscount, formatDiscountPercentage } from '@/utils/discountCalculator';
import { ArrowLeft, RefreshCw, Power, PowerOff, Star, Edit, Plus, Trash2 } from 'lucide-react';
import SubscriberList from './SubscriberList';

const PlanDetails: React.FC = () => {
  const { planName } = useParams<{ planName: string }>();
  const navigate = useNavigate();
  const [planDetails, setPlanDetails] = useState<PlanDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('monthly');
  const [processing, setProcessing] = useState<{ [key: string]: boolean }>({});
  const [showSetCurrentDialog, setShowSetCurrentDialog] = useState(false);
  const [pendingPlanId, setPendingPlanId] = useState<number | null>(null);
  const [currentPlanName, setCurrentPlanName] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [expandedTiers, setExpandedTiers] = useState<Set<string>>(new Set());
  const [showEditBaseInfoDialog, setShowEditBaseInfoDialog] = useState(false);
  const [showDeactivateUnifiedDialog, setShowDeactivateUnifiedDialog] = useState(false);
  const [freeUpgradeSettings, setFreeUpgradeSettings] = useState<FreeUpgradeSetting[]>([]);
  const [showAddTierModal, setShowAddTierModal] = useState(false);

  const fetchPlanDetails = useCallback(async (name: string) => {
    try {
      setLoading(true);
      const data = await adminPaymentService.getPlanByName(name);
      setPlanDetails(data);
      // Set default tab based on available plans
      const hasMonthly = data.monthly_plans && Object.keys(data.monthly_plans).length > 0;
      const hasAnnual = data.annual_plans && Object.keys(data.annual_plans).length > 0;
      if (!hasMonthly && hasAnnual) {
        setActiveTab('annual');
      } else if (hasMonthly) {
        setActiveTab('monthly');
      }
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to fetch plan details');
      navigate('/admin/subscription-plans');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchFreeUpgradeSettings = useCallback(async () => {
    try {
      const response = await adminPaymentService.getFreeUpgradeSettings();
      setFreeUpgradeSettings(response.settings);
    } catch (error: any) {
      console.error('Failed to fetch free upgrade settings:', error);
    }
  }, []);

  useEffect(() => {
    if (planName) {
      fetchPlanDetails(decodeURIComponent(planName));
      fetchFreeUpgradeSettings();
    }
  }, [planName, fetchPlanDetails, fetchFreeUpgradeSettings]);

  // Calculate existing tiers for the Add Tier modal
  const existingTiers = useMemo(() => {
    if (!planDetails) return [];
    const tiers = new Set<string>();
    if (planDetails.monthly_plans) {
      Object.keys(planDetails.monthly_plans).forEach(tier => tiers.add(tier));
    }
    if (planDetails.annual_plans) {
      Object.keys(planDetails.annual_plans).forEach(tier => tiers.add(tier));
    }
    return Array.from(tiers);
  }, [planDetails]);

  // Handle adding a new tier to the unified plan
  const handleAddTier = async (tierData: AddTierRequest) => {
    if (!planName) return;
    try {
      await adminPaymentService.addTierToUnifiedPlan(decodeURIComponent(planName), tierData);
      toastHelper.newMessage('success', 'Success', `${tierData.subscription_tier} tier added successfully`);
      setShowAddTierModal(false);
      fetchPlanDetails(decodeURIComponent(planName));
    } catch (error: any) {
      throw error; // Let the modal handle the error display
    }
  };

  const getFreeUpgradeSetting = (sourceTier: string, destinationTier: string, billingCycle: string): FreeUpgradeSetting | undefined => {
    return freeUpgradeSettings.find(
      s => s.source_tier === sourceTier && s.destination_tier === destinationTier && s.billing_cycle === billingCycle
    );
  };

  const handleUpdateFreeUpgradeSetting = async (
    sourceTier: string,
    destinationTier: string,
    billingCycle: string,
    enabled: boolean
  ) => {
    try {
      setProcessing({ ...processing, [`free-upgrade-${sourceTier}-${destinationTier}-${billingCycle}`]: true });
      await adminPaymentService.updateFreeUpgradeSetting(sourceTier, destinationTier, billingCycle, {
        free_upgrade_enabled: enabled
      });
      toastHelper.newMessage(
        'success',
        'Success',
        `Free upgrade ${enabled ? 'enabled' : 'disabled'} for ${sourceTier} → ${destinationTier} (${billingCycle})`
      );
      await fetchFreeUpgradeSettings();
    } catch (error: any) {
      toastHelper.newMessage(
        'error',
        'Error',
        error.response?.data?.message || 'Failed to update free upgrade setting'
      );
    } finally {
      setProcessing({ ...processing, [`free-upgrade-${sourceTier}-${destinationTier}-${billingCycle}`]: false });
    }
  };

  const handleActivate = async (planId: number) => {
    if (!planName) return;
    try {
      setProcessing({ ...processing, [`activate-${planId}`]: true });
      await adminPaymentService.activatePlan(planId);
      toastHelper.newMessage('success', 'Success', 'Plan activated successfully');
      fetchPlanDetails(decodeURIComponent(planName));
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to activate plan');
    } finally {
      setProcessing({ ...processing, [`activate-${planId}`]: false });
    }
  };


  const handleDeactivate = async (planId: number) => {
    if (!planName) return;
    try {
      setProcessing({ ...processing, [`deactivate-${planId}`]: true });
      await adminPaymentService.deactivatePlan(planId);
      toastHelper.newMessage('success', 'Success', 'Plan deactivated successfully');
      fetchPlanDetails(decodeURIComponent(planName));
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to deactivate plan');
    } finally {
      setProcessing({ ...processing, [`deactivate-${planId}`]: false });
    }
  };

  const handleActivateUnifiedPlan = async () => {
    if (!planName) return;
    try {
      setProcessing({ ...processing, 'activate-unified': true });
      const decodedPlanName = decodeURIComponent(planName);
      const result = await adminPaymentService.activateUnifiedPlan(decodedPlanName);
      toastHelper.newMessage('success', 'Success', result.message || `Successfully activated ${result.total_activated || 0} plan(s)`);
      fetchPlanDetails(decodedPlanName);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to activate unified plan');
    } finally {
      setProcessing({ ...processing, 'activate-unified': false });
    }
  };

  const handleDeactivateUnifiedPlan = async () => {
    if (!planName) return;
    try {
      setProcessing({ ...processing, 'deactivate-unified': true });
      const decodedPlanName = decodeURIComponent(planName);
      const result = await adminPaymentService.deactivateUnifiedPlan(decodedPlanName);
      toastHelper.newMessage('success', 'Success', result.message || `Successfully deactivated ${result.total_deactivated || 0} plan(s)`);
      setShowDeactivateUnifiedDialog(false);
      fetchPlanDetails(decodedPlanName);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to deactivate unified plan');
    } finally {
      setProcessing({ ...processing, 'deactivate-unified': false });
    }
  };

  const handleSetCurrentClick = async () => {
    // Get current plan name before showing dialog
    // Use the first available plan ID from the unified plan (any tier will work)
    const firstPlanId = planDetails?.monthly_plans 
      ? Object.values(planDetails.monthly_plans)[0]?.id 
      : planDetails?.annual_plans 
        ? Object.values(planDetails.annual_plans)[0]?.id 
        : null;
    
    if (!firstPlanId) {
      toastHelper.newMessage('error', 'Error', 'No plans available to set as current');
      return;
    }
    
    try {
      const currentPlanData = await adminPaymentService.getCurrentPlan();
      setCurrentPlanName(currentPlanData.current_plan?.plan_name || null);
      setPendingPlanId(firstPlanId);
      setShowSetCurrentDialog(true);
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', 'Failed to fetch current plan information');
    }
  };

  const handleSetCurrentConfirm = async () => {
    if (!planName || !pendingPlanId) return;
    try {
      setProcessing({ ...processing, [`set-current-${pendingPlanId}`]: true });
      await adminPaymentService.setCurrentPlan(pendingPlanId);
      toastHelper.newMessage('success', 'Success', 'Unified plan set as current successfully');
      setShowSetCurrentDialog(false);
      setPendingPlanId(null);
      setCurrentPlanName(null);
      fetchPlanDetails(decodeURIComponent(planName));
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to set plan as current');
    } finally {
      setProcessing({ ...processing, [`set-current-${pendingPlanId}`]: false });
    }
  };

  const handleEditPlan = async (formData: any) => {
    if (!planName || !editingPlan) return;
    try {
      setProcessing({ ...processing, [`edit-${editingPlan.id}`]: true });
      await adminPaymentService.updatePayPalPlan(editingPlan.id, formData);
      toastHelper.newMessage('success', 'Success', 'Plan updated successfully');
      setShowEditDialog(false);
      setEditingPlan(null);
      fetchPlanDetails(decodeURIComponent(planName));
    } catch (error: any) {
      toastHelper.newMessage('error', 'Error', error.response?.data?.message || 'Failed to update plan');
    } finally {
      setProcessing({ ...processing, [`edit-${editingPlan?.id}`]: false });
    }
  };

  const handleEditBaseInfo = async (formData: any) => {
    if (!planName || !planDetails) return;
    try {
      setProcessing({ ...processing, 'edit-base-info': true });
      const result = await adminPaymentService.updateUnifiedPlanBaseInfo(planDetails.name, formData);
      toastHelper.newMessage(
        'success', 
        'Success', 
        `Base information updated for ${result.data?.successful_updates || 0} plan(s)`
      );
      if (result.data?.errors && result.data.errors.length > 0) {
        toastHelper.newMessage(
          'warning',
          'Partial Success',
          `Some plans failed to update: ${result.data.errors.length} error(s)`
        );
      }
      setShowEditBaseInfoDialog(false);
      fetchPlanDetails(decodeURIComponent(planName));
    } catch (error: any) {
      const errorData = error.response?.data;
      let errorMessage = 'Failed to update base information';
      
      if (errorData?.detail) {
        const detail = errorData.detail;
        errorMessage = detail.message || errorMessage;
        
        if (detail.field_errors && detail.field_errors.length > 0) {
          const fieldErrors = detail.field_errors.map((fe: any) => 
            `${fe.field || 'Field'}: ${fe.description || fe.issue}`
          ).join('\n');
          errorMessage += `\n\n${fieldErrors}`;
        }
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      toastHelper.newMessage('error', 'Error', errorMessage);
    } finally {
      setProcessing({ ...processing, 'edit-base-info': false });
    }
  };

  const handleCreateAnnualPlan = async (annualPlanData: any) => {
    if (!planName || !planDetails?.monthly_plans || Object.keys(planDetails.monthly_plans).length === 0) return;
    try {
      setProcessing({ ...processing, 'create-annual': true });
      await adminPaymentService.createTieredPlan(annualPlanData);
      toastHelper.newMessage('success', 'Success', 'Annual plan created successfully');
      
      // Refresh plan details to show the newly created annual plan
      // The backend now returns the full plan details, so we can update immediately
      await fetchPlanDetails(decodeURIComponent(planName));
      
      // Switch to annual tab to show the newly created plan
      setActiveTab('annual');
    } catch (error: any) {
      // Handle structured PayPal errors
      const errorData = error.response?.data;
      let errorMessage = 'Failed to create annual plan';
      
      if (errorData?.detail) {
        const detail = errorData.detail;
        errorMessage = detail.message || errorMessage;
        
        // Display field-level errors if available
        if (detail.field_errors && detail.field_errors.length > 0) {
          const fieldErrors = detail.field_errors.map((fe: any) => 
            `${fe.field || 'Field'}: ${fe.description || fe.issue}`
          ).join('\n');
          errorMessage += `\n\n${fieldErrors}`;
        } else if (detail.paypal_error?.details) {
          const paypalErrors = detail.paypal_error.details.map((d: any) => 
            `${d.field || 'Field'}: ${d.description || d.issue}`
          ).join('\n');
          errorMessage += `\n\n${paypalErrors}`;
        }
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      toastHelper.newMessage('error', 'Error', errorMessage);
    } finally {
      setProcessing({ ...processing, 'create-annual': false });
    }
  };

  const renderPlanReview = (plan: any, billingCycle: string) => {
    if (!plan) {
      // Show create form for annual plan if monthly plan exists
      // Show create annual plan form if no annual plans exist
      if (billingCycle === 'annual' && planDetails?.monthly_plans && Object.keys(planDetails.monthly_plans).length > 0) {
        // Use the first monthly plan as reference (they should all have similar base settings)
        const firstMonthlyPlan = Object.values(planDetails.monthly_plans)[0];
        return <CreateAnnualPlanForm monthlyPlan={firstMonthlyPlan} planName={planDetails.name} onCreate={handleCreateAnnualPlan} processing={processing['create-annual'] || false} />;
      }
      
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No {billingCycle} plan available</p>
          </CardContent>
        </Card>
      );
    }

    const paypalPlan = plan.paypal_plan || {};
    const billingCycles = plan.billing_cycles || paypalPlan.billing_cycles || [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _regularCycle = plan.regular_billing_cycle || billingCycles.find((cycle: any) => cycle.tenure_type === 'REGULAR');
    const trialPeriods = plan.trial_periods || billingCycles.filter((cycle: any) => cycle.tenure_type === 'TRIAL');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _setupFee = plan.setup_fee;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _setupFeeFailureAction = plan.setup_fee_failure_action;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _paymentPreferences = plan.payment_preferences || paypalPlan.payment_preferences || {};
    
    // Check PayPal plan status - only ACTIVE or CREATED plans can be edited
    // Priority: paypal_plan.status (from PayPal JSON) > paypal_status (from DB) > is_active (fallback)
    // Note: paypal_plan.status is the most accurate as it comes directly from PayPal
    const paypalStatus = (paypalPlan.status || plan.paypal_status || (plan.is_active ? 'ACTIVE' : 'INACTIVE') || '').toUpperCase();
    const canEdit = paypalStatus === 'ACTIVE' || paypalStatus === 'CREATED';
    
    // For fixed-price plans, we don't have tiers anymore - each plan is a single tier
    // The price is stored directly in the plan object
    const price = plan.price;

    return (
      <div className="space-y-4 sm:space-y-6">
        <Card>
          <CardHeader className="p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base sm:text-lg">{billingCycle.charAt(0).toUpperCase() + billingCycle.slice(1)} Plan Details</CardTitle>
                <CardDescription className="text-xs sm:text-sm">Review all plan information</CardDescription>
              </div>
              <div className="flex gap-2 flex-wrap">
                {canEdit ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingPlan(plan);
                      setShowEditDialog(true);
                    }}
                    className="h-8 text-xs sm:text-sm"
                  >
                    <Edit className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                            className="h-8 text-xs sm:text-sm"
                          >
                            <Edit className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Cannot edit plan: Plan status must be ACTIVE or CREATED, but current status is {paypalStatus || 'UNKNOWN'}. Please activate the plan first.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {plan.is_active ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeactivate(plan.id)}
                    disabled={processing[`deactivate-${plan.id}`] || plan.is_current}
                    title={plan.is_current ? "Cannot deactivate a current plan. Set another plan as current first." : ""}
                    className="h-8 text-xs sm:text-sm"
                  >
                    <PowerOff className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{processing[`deactivate-${plan.id}`] ? 'Deactivating...' : 'Deactivate'}</span>
                    <span className="sm:hidden">Deact</span>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleActivate(plan.id)}
                    disabled={processing[`activate-${plan.id}`]}
                    className="h-8 text-xs sm:text-sm"
                  >
                    <Power className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{processing[`activate-${plan.id}`] ? 'Activating...' : 'Activate'}</span>
                    <span className="sm:hidden">Act</span>
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm font-semibold">Plan ID</Label>
                <p className="text-sm">{plan.id}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">PayPal Plan ID</Label>
                <p className="text-sm font-mono text-xs">{plan.paypal_plan_id}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Tier</Label>
                <p className="text-sm">{plan.tier}</p>
              </div>
              <div>
                <Label className="text-sm font-semibold">Status</Label>
                <div className="flex gap-2">
                  <Badge variant={plan.is_active ? "default" : "secondary"}>
                    {plan.is_active ? "Active" : "Inactive"}
                  </Badge>
                  {plan.is_base && (
                    <Badge variant="secondary" className="bg-[hsl(var(--secondary))] text-secondary-foreground">
                      Base
                    </Badge>
                  )}
                  {plan.is_current && (
                    <Badge variant="default" className="bg-[hsl(var(--primary))] text-primary-foreground">
                      Current
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Description</Label>
                <p className="text-sm">{plan.description || 'N/A'}</p>
              </div>
            </div>

            {plan.discount_percentage && (
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold">Discount Information</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Discount Percentage</Label>
                    <p className="text-sm">{plan.discount_percentage}%</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Discount Cause</Label>
                    <p className="text-sm">{plan.discount_cause || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}



            {/* Tier-specific information only - base info is shown at top level */}
            {price !== undefined && price !== null && (
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-4 block">Pricing</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Price</Label>
                    <p className="text-sm font-semibold">
                      {plan.currency_code || 'USD'} {price.toFixed(2)}
                    </p>
                  </div>
                  {plan.pricing_model && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Pricing Model</Label>
                      <p className="text-sm">{plan.pricing_model}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Trial periods are tier-specific, so keep them */}
            {trialPeriods && trialPeriods.length > 0 && (
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-4 block">Trial Periods</Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sequence</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Cycles</TableHead>
                      <TableHead>Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trialPeriods.map((trial: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>{trial.sequence || index + 1}</TableCell>
                        <TableCell>
                          {trial.frequency?.interval_count} {trial.frequency?.interval_unit?.toLowerCase() || 'N/A'}
                        </TableCell>
                        <TableCell>{trial.total_cycles || 'N/A'}</TableCell>
                        <TableCell>
                          {trial.currency_code || trial.pricing_scheme?.fixed_price?.currency_code || 'USD'} {trial.price || trial.pricing_scheme?.fixed_price?.value || '0'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!planDetails) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Plan not found</p>
            <Button onClick={() => navigate('/admin/subscription-plans')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Plans
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/subscription-plans')} className="h-8 px-2 sm:px-4">
            <ArrowLeft className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-3xl font-bold truncate">{planDetails.name}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">Subscription plan details</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-end sm:self-auto flex-wrap">
          {planDetails && (() => {
            // Check if any tier is current (all tiers in a unified plan share the same is_current status)
            const isCurrent = planDetails.monthly_plans 
              ? Object.values(planDetails.monthly_plans).some(p => p.is_current)
              : planDetails.annual_plans
                ? Object.values(planDetails.annual_plans).some(p => p.is_current)
                : false;
            
            // Check both monthly and annual plans for active status
            const hasActiveMonthly = planDetails.monthly_plans && Object.keys(planDetails.monthly_plans).length > 0
              ? Object.values(planDetails.monthly_plans).some(p => p.is_active === true)
              : false;
            const hasActiveAnnual = planDetails.annual_plans && Object.keys(planDetails.annual_plans).length > 0
              ? Object.values(planDetails.annual_plans).some(p => p.is_active === true)
              : false;
            const hasActivePlans = hasActiveMonthly || hasActiveAnnual;
            
            // Check for inactive plans (to show activate button)
            const hasInactiveMonthly = planDetails.monthly_plans && Object.keys(planDetails.monthly_plans).length > 0
              ? Object.values(planDetails.monthly_plans).some(p => p.is_active === false)
              : false;
            const hasInactiveAnnual = planDetails.annual_plans && Object.keys(planDetails.annual_plans).length > 0
              ? Object.values(planDetails.annual_plans).some(p => p.is_active === false)
              : false;
            const hasInactivePlans = hasInactiveMonthly || hasInactiveAnnual;

            return (
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={isCurrent ? "default" : "outline"}
                  size="sm"
                  onClick={handleSetCurrentClick}
                  disabled={processing[`set-current-${pendingPlanId}`] || isCurrent}
                  className="h-8 text-xs sm:text-sm"
                >
                  <Star className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{processing[`set-current-${pendingPlanId}`] ? 'Setting...' : isCurrent ? 'Current Plan' : 'Set as Current'}</span>
                  <span className="sm:hidden">{isCurrent ? 'Current' : 'Set'}</span>
                </Button>
                {hasActivePlans && !isCurrent && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDeactivateUnifiedDialog(true)}
                    disabled={processing['deactivate-unified']}
                    className="h-8 text-xs sm:text-sm"
                  >
                    <PowerOff className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{processing['deactivate-unified'] ? 'Deactivating...' : 'Deactivate Plan'}</span>
                    <span className="sm:hidden">Deactivate</span>
                  </Button>
                )}
                {hasInactivePlans && !isCurrent && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleActivateUnifiedPlan}
                    disabled={processing['activate-unified']}
                    className="h-8 text-xs sm:text-sm"
                  >
                    <Power className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{processing['activate-unified'] ? 'Activating...' : 'Activate Plan'}</span>
                    <span className="sm:hidden">Activate</span>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddTierModal(true)}
                  className="h-8 text-xs sm:text-sm"
                >
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Add Tier</span>
                  <span className="sm:hidden">+ Tier</span>
                </Button>
              </div>
            );
          })()}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 gap-1 sm:gap-0 max-w-5xl h-auto p-1">
          <TabsTrigger value="monthly" disabled={!planDetails.monthly_plans || Object.keys(planDetails.monthly_plans).length === 0} className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Monthly Plan Review
          </TabsTrigger>
          <TabsTrigger value="annual" className="text-xs sm:text-sm px-2 py-2 sm:px-3">
            Annual Plan Review
          </TabsTrigger>
          <TabsTrigger value="subscribers" className="text-xs sm:text-sm px-2 py-2 sm:px-3 col-span-2 sm:col-span-1">
            Subscribers List
          </TabsTrigger>
        </TabsList>
        <TabsContent value="monthly" className="mt-6">
          {(() => {
            const expectedTiers = ['starter', 'pro', 'family']; // Exclude 'free'
            const monthlyPlans = planDetails.monthly_plans || {};
            const firstPlan = Object.values(monthlyPlans)[0] as any;
            
            // Extract base information from first plan (should be same across all tiers)
            const baseInfo = firstPlan ? {
              currency: firstPlan.currency_code || 'USD',
              taxPercentage: firstPlan.tax_percentage ?? 0,
              taxInclusive: firstPlan.tax_inclusive ?? false,
              setupFee: firstPlan.setup_fee ?? 0,
              setupFeeFailureAction: firstPlan.setup_fee_failure_action || 'CONTINUE',
              paymentFailureThreshold: firstPlan.payment_preferences?.payment_failure_threshold ?? 1,
              autoBillOutstanding: firstPlan.payment_preferences?.auto_bill_outstanding ?? true,
              billingFrequency: firstPlan.regular_billing_cycle?.frequency,
              totalCycles: firstPlan.regular_billing_cycle?.total_cycles ?? 0,
            } : null;
            
            return (
              <div className="space-y-6">
                {/* Base Information */}
                {baseInfo && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Base Plan Information</CardTitle>
                          <CardDescription>Common settings for all monthly tiers</CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowEditBaseInfoDialog(true)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Base Info
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold">Currency</Label>
                          <p className="text-sm">{baseInfo.currency}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Tax Percentage</Label>
                          <p className="text-sm">{baseInfo.taxPercentage}%</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Tax Inclusive</Label>
                          <p className="text-sm">{baseInfo.taxInclusive ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Setup Fee</Label>
                          <p className="text-sm">
                            {baseInfo.currency} {baseInfo.setupFee?.toFixed ? baseInfo.setupFee.toFixed(2) : baseInfo.setupFee}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Setup Fee Failure Action</Label>
                          <p className="text-sm">{baseInfo.setupFeeFailureAction}</p>
                        </div>
                        {baseInfo.billingFrequency && (
                          <div>
                            <Label className="text-sm font-semibold">Billing Frequency</Label>
                            <p className="text-sm">Every {baseInfo.billingFrequency.interval_count} {baseInfo.billingFrequency.interval_unit}</p>
                          </div>
                        )}
                        <div>
                          <Label className="text-sm font-semibold">Total Cycles</Label>
                          <p className="text-sm">{baseInfo.totalCycles === 0 ? 'Unlimited' : baseInfo.totalCycles}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Payment Failure Threshold</Label>
                          <p className="text-sm">{baseInfo.paymentFailureThreshold} missed cycle(s)</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Auto Bill Outstanding</Label>
                          <p className="text-sm">{baseInfo.autoBillOutstanding ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Free Upgrade Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle>Free Upgrade Settings</CardTitle>
                    <CardDescription>Configure free upgrade promotions per tier and billing cycle</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Starter Monthly → Pro Monthly */}
                      {(() => {
                        const setting = getFreeUpgradeSetting('starter', 'pro', 'monthly');
                        const key = 'free-upgrade-starter-pro-monthly';
                        return (
                          <div className="flex items-center justify-between border-b pb-4">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-semibold">Starter Monthly → Pro Monthly</Label>
                              <p className="text-xs text-muted-foreground">
                                Enable free upgrade from Starter to Pro for monthly subscriptions
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={setting?.free_upgrade_enabled || false}
                                onCheckedChange={(checked) => handleUpdateFreeUpgradeSetting('starter', 'pro', 'monthly', checked)}
                                disabled={processing[key]}
                                className={!setting?.free_upgrade_enabled ? 'ring-2 ring-muted-foreground/30' : ''}
                              />
                              <div className="min-w-[60px] flex justify-end">
                                {setting?.free_upgrade_enabled && (
                                  <Badge variant="default" className="bg-green-500">
                                    Active
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Starter Annual → Pro Annual */}
                      {(() => {
                        const setting = getFreeUpgradeSetting('starter', 'pro', 'annual');
                        const key = 'free-upgrade-starter-pro-annual';
                        return (
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-semibold">Starter Annual → Pro Annual</Label>
                              <p className="text-xs text-muted-foreground">
                                Enable free upgrade from Starter to Pro for annual subscriptions
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={setting?.free_upgrade_enabled || false}
                                onCheckedChange={(checked) => handleUpdateFreeUpgradeSetting('starter', 'pro', 'annual', checked)}
                                disabled={processing[key]}
                                className={!setting?.free_upgrade_enabled ? 'ring-2 ring-muted-foreground/30' : ''}
                              />
                              <div className="min-w-[60px] flex justify-end">
                                {setting?.free_upgrade_enabled && (
                                  <Badge variant="default" className="bg-green-500">
                                    Active
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Tiers Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Tiers</CardTitle>
                    <CardDescription>Individual tier pricing and details</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3 p-3">
                      {expectedTiers.map((tierName) => {
                        const tierPlan = monthlyPlans[tierName];
                        const isExpanded = expandedTiers.has(`monthly-${tierName}`);
                        const toggleExpanded = () => {
                          const key = `monthly-${tierName}`;
                          setExpandedTiers(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) {
                              next.delete(key);
                            } else {
                              next.add(key);
                            }
                            return next;
                          });
                        };

                        if (!tierPlan) {
                          return (
                            <Card key={tierName} className="p-3 bg-muted/20">
                              <div className="text-center text-muted-foreground py-4">
                                <p className="font-medium capitalize">{tierName}</p>
                                <p className="text-xs mt-1">No plan configured</p>
                              </div>
                            </Card>
                          );
                        }

                        const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined
                          ? tierPlan.discounted_price
                          : tierPlan.base_price;
                        const basePrice = tierPlan.base_price;
                        const hasDiscount = basePrice !== null && basePrice !== undefined && discountedPrice !== null && discountedPrice !== undefined && basePrice > discountedPrice;
                        const promotionDiscount = calculatePromotionDiscount(basePrice, discountedPrice);

                        return (
                          <Card key={tierName} className="p-3">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h3 className="font-semibold capitalize text-base">{tierName}</h3>
                                    {promotionDiscount !== null && (
                                      <Badge variant="default" className="bg-green-500 text-xs">
                                        {formatDiscountPercentage(promotionDiscount)} OFF
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-baseline gap-2">
                                      <span className="text-xl font-bold">
                                        {tierPlan.currency_code || baseInfo?.currency || 'USD'} {discountedPrice !== null && discountedPrice !== undefined ? discountedPrice.toFixed(2) : 'N/A'}
                                      </span>
                                      {hasDiscount && basePrice !== null && basePrice !== undefined && (
                                        <span className="text-sm line-through text-muted-foreground">
                                          {tierPlan.currency_code || baseInfo?.currency || 'USD'} {basePrice.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingPlan(tierPlan);
                                    setShowEditDialog(true);
                                  }}
                                  className="h-8 shrink-0"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-muted-foreground">Plan ID:</span>
                                  <p className="font-mono">{tierPlan.id}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">PayPal ID:</span>
                                  <p className="font-mono truncate">{tierPlan.paypal_plan_id}</p>
                                </div>
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={toggleExpanded}
                                className="w-full h-8 text-xs"
                              >
                                {isExpanded ? '− Hide Details' : '+ Show Details'}
                              </Button>

                              {isExpanded && (
                                <div className="pt-2 border-t">
                                  {renderPlanReview(tierPlan, 'monthly')}
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tier Name</TableHead>
                            <TableHead>Discounted Price</TableHead>
                            <TableHead>Base Price</TableHead>
                            <TableHead>Discount %</TableHead>
                            <TableHead>Plan ID</TableHead>
                            <TableHead>PayPal Plan ID</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {expectedTiers.map((tierName) => {
                          const tierPlan = monthlyPlans[tierName];
                          const isExpanded = expandedTiers.has(`monthly-${tierName}`);
                          const toggleExpanded = () => {
                            const key = `monthly-${tierName}`;
                            setExpandedTiers(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) {
                                next.delete(key);
                              } else {
                                next.add(key);
                              }
                              return next;
                            });
                          };
                          
                          return (
                            <React.Fragment key={tierName}>
                              <TableRow className="cursor-pointer hover:bg-muted/50" onClick={tierPlan ? toggleExpanded : undefined}>
                                <TableCell className="font-medium capitalize">
                                  {tierPlan && (
                                    <Button variant="ghost" size="sm" className="mr-2 h-6 w-6 p-0">
                                      {isExpanded ? '−' : '+'}
                                    </Button>
                                  )}
                                  {tierName}
                                </TableCell>
                                <TableCell>
                                  {tierPlan ? (
                                    (() => {
                                      const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined 
                                        ? tierPlan.discounted_price 
                                        : tierPlan.base_price;
                                      
                                      return (
                                        <span className="font-semibold">
                                          {tierPlan.currency_code || baseInfo?.currency || 'USD'} {discountedPrice !== null && discountedPrice !== undefined ? discountedPrice.toFixed(2) : 'N/A'}
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {tierPlan ? (
                                    (() => {
                                      const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined 
                                        ? tierPlan.discounted_price 
                                        : tierPlan.base_price;
                                      const basePrice = tierPlan.base_price;
                                      const hasDiscount = basePrice !== null && basePrice !== undefined && discountedPrice !== null && discountedPrice !== undefined && basePrice > discountedPrice;
                                      
                                      if (basePrice !== null && basePrice !== undefined) {
                                        return (
                                          <span className={hasDiscount ? "line-through text-muted-foreground" : "font-medium"}>
                                            {tierPlan.currency_code || baseInfo?.currency || 'USD'} {basePrice.toFixed(2)}
                                          </span>
                                        );
                                      } else {
                                        return <span className="text-muted-foreground">N/A</span>;
                                      }
                                    })()
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {tierPlan ? (
                                    (() => {
                                      const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined 
                                        ? tierPlan.discounted_price 
                                        : tierPlan.base_price;
                                      const basePrice = tierPlan.base_price;
                                      const promotionDiscount = calculatePromotionDiscount(basePrice, discountedPrice);
                                      
                                      if (promotionDiscount !== null) {
                                        return (
                                          <Badge variant="default" className="bg-green-500">
                                            {formatDiscountPercentage(promotionDiscount)} OFF
                                          </Badge>
                                        );
                                      } else {
                                        return <span className="text-muted-foreground text-sm">No discount</span>;
                                      }
                                    })()
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                  {tierPlan?.id || '-'}
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                  {tierPlan?.paypal_plan_id || '-'}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {tierPlan && (
                                    <div className="flex gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingPlan(tierPlan);
                                          setShowEditDialog(true);
                                        }}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                              {tierPlan && isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={7} className="bg-muted/30">
                                    <div className="py-4 px-4">
                                      {renderPlanReview(tierPlan, 'monthly')}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}
        </TabsContent>
        <TabsContent value="annual" className="mt-6">
          {(() => {
            const expectedTiers = ['starter', 'pro', 'family']; // Exclude 'free'
            const annualPlans = planDetails.annual_plans || {};
            const hasAnyAnnual = Object.keys(annualPlans).length > 0;
            
            if (!hasAnyAnnual) {
              return renderPlanReview(null, 'annual');
            }
            
            const firstPlan = Object.values(annualPlans)[0] as any;
            
            // Extract base information from first plan
            const baseInfo = firstPlan ? {
              currency: firstPlan.currency_code || 'USD',
              taxPercentage: firstPlan.tax_percentage ?? 0,
              taxInclusive: firstPlan.tax_inclusive ?? false,
              setupFee: firstPlan.setup_fee ?? 0,
              setupFeeFailureAction: firstPlan.setup_fee_failure_action || 'CONTINUE',
              paymentFailureThreshold: firstPlan.payment_preferences?.payment_failure_threshold ?? 1,
              autoBillOutstanding: firstPlan.payment_preferences?.auto_bill_outstanding ?? true,
              billingFrequency: firstPlan.regular_billing_cycle?.frequency,
              totalCycles: firstPlan.regular_billing_cycle?.total_cycles ?? 0,
            } : null;
            
            return (
              <div className="space-y-6">
                {/* Base Information */}
                {baseInfo && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Base Plan Information</CardTitle>
                          <CardDescription>Common settings for all annual tiers</CardDescription>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowEditBaseInfoDialog(true)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Base Info
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold">Currency</Label>
                          <p className="text-sm">{baseInfo.currency}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Tax Percentage</Label>
                          <p className="text-sm">{baseInfo.taxPercentage}%</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Tax Inclusive</Label>
                          <p className="text-sm">{baseInfo.taxInclusive ? 'Yes' : 'No'}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Setup Fee</Label>
                          <p className="text-sm">
                            {baseInfo.currency} {baseInfo.setupFee?.toFixed ? baseInfo.setupFee.toFixed(2) : baseInfo.setupFee}
                          </p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Setup Fee Failure Action</Label>
                          <p className="text-sm">{baseInfo.setupFeeFailureAction}</p>
                        </div>
                        {baseInfo.billingFrequency && (
                          <div>
                            <Label className="text-sm font-semibold">Billing Frequency</Label>
                            <p className="text-sm">Every {baseInfo.billingFrequency.interval_count} {baseInfo.billingFrequency.interval_unit}</p>
                          </div>
                        )}
                        <div>
                          <Label className="text-sm font-semibold">Total Cycles</Label>
                          <p className="text-sm">{baseInfo.totalCycles === 0 ? 'Unlimited' : baseInfo.totalCycles}</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Payment Failure Threshold</Label>
                          <p className="text-sm">{baseInfo.paymentFailureThreshold} missed cycle(s)</p>
                        </div>
                        <div>
                          <Label className="text-sm font-semibold">Auto Bill Outstanding</Label>
                          <p className="text-sm">{baseInfo.autoBillOutstanding ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Free Upgrade Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle>Free Upgrade Settings</CardTitle>
                    <CardDescription>Configure free upgrade promotions per tier and billing cycle</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Starter Monthly → Pro Monthly */}
                      {(() => {
                        const setting = getFreeUpgradeSetting('starter', 'pro', 'monthly');
                        const key = 'free-upgrade-starter-pro-monthly';
                        return (
                          <div className="flex items-center justify-between border-b pb-4">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-semibold">Starter Monthly → Pro Monthly</Label>
                              <p className="text-xs text-muted-foreground">
                                Enable free upgrade from Starter to Pro for monthly subscriptions
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={setting?.free_upgrade_enabled || false}
                                onCheckedChange={(checked) => handleUpdateFreeUpgradeSetting('starter', 'pro', 'monthly', checked)}
                                disabled={processing[key]}
                                className={!setting?.free_upgrade_enabled ? 'ring-2 ring-muted-foreground/30' : ''}
                              />
                              <div className="min-w-[60px] flex justify-end">
                                {setting?.free_upgrade_enabled && (
                                  <Badge variant="default" className="bg-green-500">
                                    Active
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Starter Annual → Pro Annual */}
                      {(() => {
                        const setting = getFreeUpgradeSetting('starter', 'pro', 'annual');
                        const key = 'free-upgrade-starter-pro-annual';
                        return (
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <Label className="text-sm font-semibold">Starter Annual → Pro Annual</Label>
                              <p className="text-xs text-muted-foreground">
                                Enable free upgrade from Starter to Pro for annual subscriptions
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <Switch
                                checked={setting?.free_upgrade_enabled || false}
                                onCheckedChange={(checked) => handleUpdateFreeUpgradeSetting('starter', 'pro', 'annual', checked)}
                                disabled={processing[key]}
                                className={!setting?.free_upgrade_enabled ? 'ring-2 ring-muted-foreground/30' : ''}
                              />
                              <div className="min-w-[60px] flex justify-end">
                                {setting?.free_upgrade_enabled && (
                                  <Badge variant="default" className="bg-green-500">
                                    Active
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Tiers Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Annual Tiers</CardTitle>
                    <CardDescription>Individual tier pricing and details</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3 p-3">
                      {expectedTiers.map((tierName) => {
                        const tierPlan = annualPlans[tierName];
                        const isExpanded = expandedTiers.has(`annual-${tierName}`);
                        const toggleExpanded = () => {
                          const key = `annual-${tierName}`;
                          setExpandedTiers(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) {
                              next.delete(key);
                            } else {
                              next.add(key);
                            }
                            return next;
                          });
                        };

                        if (!tierPlan) {
                          return (
                            <Card key={tierName} className="p-3 bg-muted/20">
                              <div className="text-center text-muted-foreground py-4">
                                <p className="font-medium capitalize">{tierName}</p>
                                <p className="text-xs mt-1">No plan configured</p>
                              </div>
                            </Card>
                          );
                        }

                        const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined
                          ? tierPlan.discounted_price
                          : tierPlan.base_price;
                        const basePrice = tierPlan.base_price;
                        const twelveXPrice = tierPlan.twelve_x_monthly_price;

                        const monthlyPlan = planDetails?.monthly_plans
                          ? Object.values(planDetails.monthly_plans).find(p => p.tier === tierPlan.tier)
                          : null;

                        const discounts = [];
                        const promotionDiscount = calculatePromotionDiscount(basePrice, discountedPrice);
                        if (promotionDiscount !== null) {
                          discounts.push({ label: 'Promotion', percent: promotionDiscount });
                        }

                        if (monthlyPlan) {
                          const annualSavings = calculateAnnualSavings(
                            discountedPrice,
                            monthlyPlan.discounted_price ?? monthlyPlan.base_price
                          );
                          if (annualSavings !== null) {
                            discounts.push({ label: 'Annual', percent: annualSavings });
                          }

                          const combinedDiscount = calculateCombinedDiscount(
                            discountedPrice,
                            monthlyPlan.base_price
                          );
                          if (combinedDiscount !== null) {
                            discounts.push({ label: 'Combined', percent: combinedDiscount });
                          }
                        }

                        return (
                          <Card key={tierName} className="p-3">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <h3 className="font-semibold capitalize text-base">{tierName}</h3>
                                    {discounts.map((discount, idx) => (
                                      <Badge key={idx} variant="default" className="bg-green-500 text-xs">
                                        {discount.label}: {formatDiscountPercentage(discount.percent)}
                                      </Badge>
                                    ))}
                                  </div>
                                  <div className="space-y-1">
                                    <div className="flex items-baseline gap-2 flex-wrap">
                                      <span className="text-xl font-bold">
                                        {tierPlan.currency_code || baseInfo?.currency || 'USD'} {discountedPrice !== null && discountedPrice !== undefined ? discountedPrice.toFixed(2) : 'N/A'}
                                      </span>
                                      {basePrice !== null && basePrice !== undefined && basePrice > discountedPrice && (
                                        <span className="text-sm line-through text-muted-foreground">
                                          {tierPlan.currency_code || baseInfo?.currency || 'USD'} {basePrice.toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                    {twelveXPrice !== null && twelveXPrice !== undefined && (
                                      <p className="text-xs text-muted-foreground">
                                        vs 12x Monthly: {tierPlan.currency_code || baseInfo?.currency || 'USD'} {twelveXPrice.toFixed(2)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingPlan(tierPlan);
                                    setShowEditDialog(true);
                                  }}
                                  className="h-8 shrink-0"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-muted-foreground">Plan ID:</span>
                                  <p className="font-mono">{tierPlan.id}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">PayPal ID:</span>
                                  <p className="font-mono truncate">{tierPlan.paypal_plan_id}</p>
                                </div>
                              </div>

                              <Button
                                variant="outline"
                                size="sm"
                                onClick={toggleExpanded}
                                className="w-full h-8 text-xs"
                              >
                                {isExpanded ? '− Hide Details' : '+ Show Details'}
                              </Button>

                              {isExpanded && (
                                <div className="pt-2 border-t">
                                  {renderPlanReview(tierPlan, 'annual')}
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tier Name</TableHead>
                            <TableHead>Discounted Price</TableHead>
                            <TableHead>Base Price</TableHead>
                            <TableHead>12x Monthly Price</TableHead>
                            <TableHead>Discount %</TableHead>
                            <TableHead>Plan ID</TableHead>
                            <TableHead>PayPal Plan ID</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {expectedTiers.map((tierName) => {
                          const tierPlan = annualPlans[tierName];
                          const isExpanded = expandedTiers.has(`annual-${tierName}`);
                          const toggleExpanded = () => {
                            const key = `annual-${tierName}`;
                            setExpandedTiers(prev => {
                              const next = new Set(prev);
                              if (next.has(key)) {
                                next.delete(key);
                              } else {
                                next.add(key);
                              }
                              return next;
                            });
                          };
                          
                          return (
                            <React.Fragment key={tierName}>
                              <TableRow className="cursor-pointer hover:bg-muted/50" onClick={tierPlan ? toggleExpanded : undefined}>
                                <TableCell className="font-medium capitalize">
                                  {tierPlan && (
                                    <Button variant="ghost" size="sm" className="mr-2 h-6 w-6 p-0">
                                      {isExpanded ? '−' : '+'}
                                    </Button>
                                  )}
                                  {tierName}
                                </TableCell>
                                <TableCell>
                                  {tierPlan ? (
                                    (() => {
                                      const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined 
                                        ? tierPlan.discounted_price 
                                        : tierPlan.base_price;
                                      
                                      return (
                                        <span className="font-semibold">
                                          {tierPlan.currency_code || baseInfo?.currency || 'USD'} {discountedPrice !== null && discountedPrice !== undefined ? discountedPrice.toFixed(2) : 'N/A'}
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {tierPlan ? (
                                    (() => {
                                      const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined 
                                        ? tierPlan.discounted_price 
                                        : tierPlan.base_price;
                                      const basePrice = tierPlan.base_price;
                                      const hasDiscount = basePrice !== null && basePrice !== undefined && discountedPrice !== null && discountedPrice !== undefined && basePrice > discountedPrice;
                                      
                                      if (basePrice !== null && basePrice !== undefined) {
                                        return (
                                          <span className={hasDiscount ? "line-through text-muted-foreground" : "font-medium"}>
                                            {tierPlan.currency_code || baseInfo?.currency || 'USD'} {basePrice.toFixed(2)}
                                          </span>
                                        );
                                      } else {
                                        return <span className="text-muted-foreground">N/A</span>;
                                      }
                                    })()
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {tierPlan ? (
                                    (() => {
                                      const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined 
                                        ? tierPlan.discounted_price 
                                        : tierPlan.base_price;
                                      const twelveXPrice = tierPlan.twelve_x_monthly_price;
                                      const hasDiscount = twelveXPrice !== null && twelveXPrice !== undefined && discountedPrice !== null && discountedPrice !== undefined && twelveXPrice > discountedPrice;
                                      
                                      if (twelveXPrice !== null && twelveXPrice !== undefined) {
                                        return (
                                          <span className={hasDiscount ? "line-through text-muted-foreground" : "font-medium"}>
                                            {tierPlan.currency_code || baseInfo?.currency || 'USD'} {twelveXPrice.toFixed(2)}
                                          </span>
                                        );
                                      } else {
                                        return <span className="text-muted-foreground">N/A</span>;
                                      }
                                    })()
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {tierPlan ? (
                                    (() => {
                                      const discountedPrice = tierPlan.discounted_price !== null && tierPlan.discounted_price !== undefined 
                                        ? tierPlan.discounted_price 
                                        : tierPlan.base_price;
                                      const basePrice = tierPlan.base_price;
                                      
                                      // Find corresponding monthly plan for annual plans
                                      const monthlyPlan = activeTab === 'annual' && planDetails?.monthly_plans 
                                        ? Object.values(planDetails.monthly_plans).find(p => p.tier === tierPlan.tier)
                                        : null;
                                      
                                      const discounts = [];
                                      
                                      // Promotion discount (base_price vs discounted_price)
                                      const promotionDiscount = calculatePromotionDiscount(basePrice, discountedPrice);
                                      if (promotionDiscount !== null) {
                                        discounts.push({ label: 'Promotion', percent: promotionDiscount });
                                      }
                                      
                                      // For annual plans, calculate annual savings and combined discount
                                      if (activeTab === 'annual' && monthlyPlan) {
                                        // Annual savings: compare annual discounted vs 12x monthly discounted
                                        const annualSavings = calculateAnnualSavings(
                                          discountedPrice,
                                          monthlyPlan.discounted_price ?? monthlyPlan.base_price
                                        );
                                        if (annualSavings !== null) {
                                          discounts.push({ label: 'Annual', percent: annualSavings });
                                        }
                                        
                                        // Combined discount: compare annual discounted vs 12x monthly base
                                        const combinedDiscount = calculateCombinedDiscount(
                                          discountedPrice,
                                          monthlyPlan.base_price
                                        );
                                        if (combinedDiscount !== null) {
                                          discounts.push({ label: 'Combined', percent: combinedDiscount });
                                        }
                                      }
                                      
                                      if (discounts.length > 0) {
                                        return (
                                          <div className="flex flex-col gap-1">
                                            {discounts.map((discount, idx) => (
                                              <Badge key={idx} variant="default" className="bg-green-500 text-xs">
                                                {discount.label}: {formatDiscountPercentage(discount.percent)}
                                              </Badge>
                                            ))}
                                          </div>
                                        );
                                      } else {
                                        return <span className="text-muted-foreground text-sm">No discount</span>;
                                      }
                                    })()
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                  {tierPlan?.id || '-'}
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                  {tierPlan?.paypal_plan_id || '-'}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {tierPlan && (
                                    <div className="flex gap-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingPlan(tierPlan);
                                          setShowEditDialog(true);
                                        }}
                                      >
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                              {tierPlan && isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={8} className="bg-muted/30">
                                    <div className="py-4 px-4">
                                      {renderPlanReview(tierPlan, 'annual')}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}
        </TabsContent>
        <TabsContent value="subscribers" className="mt-6">
          {planDetails && (
            <SubscriberList
              monthlySubscribers={planDetails.monthly_subscribers || []}
              annualSubscribers={planDetails.annual_subscribers || []}
              totalSubscribers={planDetails.total_subscribers || 0}
              hasMonthlyPlans={planDetails.monthly_plans && Object.keys(planDetails.monthly_plans).length > 0}
              hasAnnualPlans={planDetails.annual_plans && Object.keys(planDetails.annual_plans).length > 0}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Deactivate Unified Plan Dialog */}
      <Dialog open={showDeactivateUnifiedDialog} onOpenChange={setShowDeactivateUnifiedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Unified Plan</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate all plans in "{planDetails?.plan_name}"? This will deactivate all nested PayPal plans (all tiers, monthly and annual) with one click.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeactivateUnifiedDialog(false)}
              disabled={processing['deactivate-unified']}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeactivateUnifiedPlan}
              disabled={processing['deactivate-unified']}
            >
              {processing['deactivate-unified'] ? 'Deactivating...' : 'Deactivate All Plans'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSetCurrentDialog} onOpenChange={setShowSetCurrentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Unified Plan as Current</DialogTitle>
            <DialogDescription>
              {currentPlanName ? (
                <>
                  At the moment <strong>{currentPlanName}</strong> is the current plan. By proceeding, you will replace <strong>{currentPlanName}</strong> as the current plan.
                  <br /><br />
                  This will set all monthly and annual tiers of <strong>{planDetails.name}</strong> as the current plan displayed to users.
                </>
              ) : (
                <>
                  This unified plan (<strong>{planDetails.name}</strong>) will be set as the current plan displayed to users.
                  <br /><br />
                  This will set all monthly and annual tiers as current.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowSetCurrentDialog(false);
              setPendingPlanId(null);
              setCurrentPlanName(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSetCurrentConfirm} disabled={processing[`set-current-${pendingPlanId}`]}>
              {processing[`set-current-${pendingPlanId}`] ? 'Setting...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditPlanDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        plan={editingPlan}
        onSave={handleEditPlan}
        processing={processing[`edit-${editingPlan?.id}`] || false}
      />

      <EditBaseInfoDialog
        open={showEditBaseInfoDialog}
        onOpenChange={setShowEditBaseInfoDialog}
        planDetails={planDetails}
        onSave={handleEditBaseInfo}
        processing={processing['edit-base-info'] || false}
      />

      {showAddTierModal && (
        <AddTierModal
          planName={decodeURIComponent(planName || '')}
          existingTiers={existingTiers}
          onAdd={handleAddTier}
          onClose={() => setShowAddTierModal(false)}
        />
      )}

    </div>
  );
};

interface EditPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: any;
  onSave: (formData: any) => void;
  processing: boolean;
}

const EditPlanDialog: React.FC<EditPlanDialogProps> = ({ open, onOpenChange, plan, onSave, processing }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    payment_preferences: {
      auto_bill_outstanding: true,
      setup_fee: { value: '', currency_code: 'USD' },
      setup_fee_failure_action: 'CONTINUE',
      payment_failure_threshold: 1
    },
    taxes: {
      percentage: ''
    }
  });

  useEffect(() => {
    if (plan) {
      const paypalPlan = plan.paypal_plan || {};
      const paymentPrefs = paypalPlan.payment_preferences || plan.payment_preferences || {};
      const taxes = paypalPlan.taxes || (plan.tax_percentage ? {
        percentage: plan.tax_percentage.toString(),
        inclusive: plan.tax_inclusive
      } : {});
      
      setFormData({
        name: plan.paypal_name || plan.paypal_plan_name || '',
        description: plan.paypal_description || plan.description || '',
        payment_preferences: {
          auto_bill_outstanding: paymentPrefs.auto_bill_outstanding !== undefined ? paymentPrefs.auto_bill_outstanding : true,
          setup_fee: paymentPrefs.setup_fee || { value: plan.setup_fee?.toString() || '', currency_code: 'USD' },
          setup_fee_failure_action: paymentPrefs.setup_fee_failure_action || 'CONTINUE',
          payment_failure_threshold: paymentPrefs.payment_failure_threshold || 1
        },
        taxes: {
          percentage: taxes.percentage || plan.tax_percentage?.toString() || ''
        }
      });
    }
  }, [plan]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updateData: any = {};
    
    if (formData.name) updateData.name = formData.name;
    if (formData.description) updateData.description = formData.description;
    
    // Build payment preferences
    const paymentPrefs: any = {};
    if (formData.payment_preferences.auto_bill_outstanding !== undefined) {
      paymentPrefs.auto_bill_outstanding = formData.payment_preferences.auto_bill_outstanding;
    }
    if (formData.payment_preferences.setup_fee.value) {
      paymentPrefs.setup_fee = {
        value: formData.payment_preferences.setup_fee.value,
        currency_code: formData.payment_preferences.setup_fee.currency_code
      };
    }
    if (formData.payment_preferences.setup_fee_failure_action) {
      paymentPrefs.setup_fee_failure_action = formData.payment_preferences.setup_fee_failure_action;
    }
    if (formData.payment_preferences.payment_failure_threshold !== undefined) {
      paymentPrefs.payment_failure_threshold = formData.payment_preferences.payment_failure_threshold;
    }
    if (Object.keys(paymentPrefs).length > 0) {
      updateData.payment_preferences = paymentPrefs;
    }
    
    // Build taxes
    if (formData.taxes.percentage) {
      updateData.taxes = {
        percentage: formData.taxes.percentage,
        inclusive: formData.taxes.inclusive
      };
    }
    
    onSave(updateData);
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Plan</DialogTitle>
          <DialogDescription>
            Update PayPal plan fields. Only provided fields will be updated.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Plan Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter plan name"
                maxLength={127}
              />
            </div>
            
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter plan description"
                maxLength={127}
                rows={3}
              />
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">Payment Preferences</Label>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto_bill">Auto Bill Outstanding</Label>
                <p className="text-sm text-muted-foreground">Automatically bill outstanding amounts</p>
              </div>
              <Switch
                id="auto_bill"
                checked={formData.payment_preferences.auto_bill_outstanding}
                onCheckedChange={(checked) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    auto_bill_outstanding: checked
                  }
                })}
              />
            </div>

            <div>
              <Label htmlFor="setup_fee">Setup Fee</Label>
              <Input
                id="setup_fee"
                type="number"
                step="0.01"
                value={formData.payment_preferences.setup_fee.value}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    setup_fee: {
                      ...formData.payment_preferences.setup_fee,
                      value: e.target.value
                    }
                  }
                })}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label htmlFor="failure_action">Setup Fee Failure Action</Label>
              <select
                id="failure_action"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                value={formData.payment_preferences.setup_fee_failure_action}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    setup_fee_failure_action: e.target.value
                  }
                })}
              >
                <option value="CONTINUE">Continue</option>
                <option value="CANCEL">Cancel</option>
              </select>
            </div>

            <div>
              <Label htmlFor="failure_threshold">Payment Failure Threshold</Label>
              <Input
                id="failure_threshold"
                type="number"
                min="1"
                value={formData.payment_preferences.payment_failure_threshold}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    payment_failure_threshold: parseInt(e.target.value) || 1
                  }
                })}
                placeholder="1"
              />
              <p className="text-sm text-muted-foreground mt-1">Number of missed cycles before pausing subscription</p>
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">Tax Configuration</Label>
            
            <div>
              <Label htmlFor="tax_percentage">Tax Percentage</Label>
              <Input
                id="tax_percentage"
                type="number"
                step="0.01"
                value={formData.taxes.percentage}
                onChange={(e) => setFormData({
                  ...formData,
                  taxes: {
                    ...formData.taxes,
                    percentage: e.target.value
                  }
                })}
                placeholder="0.00"
              />
              <p className="text-sm text-muted-foreground mt-1">Note: Only tax percentage can be updated. Tax inclusive setting cannot be changed via API.</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Updating...' : 'Update Plan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface EditBaseInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planDetails: PlanDetailsResponse | null;
  onSave: (formData: any) => void;
  processing: boolean;
}

const EditBaseInfoDialog: React.FC<EditBaseInfoDialogProps> = ({ open, onOpenChange, planDetails, onSave, processing }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    payment_preferences: {
      auto_bill_outstanding: true,
      setup_fee: { value: '', currency_code: 'USD' },
      setup_fee_failure_action: 'CONTINUE',
      payment_failure_threshold: 1
    },
    taxes: {
      percentage: ''
    }
  });

  useEffect(() => {
    if (planDetails) {
      // Get base info from first available plan (monthly or annual)
      const firstPlan = Object.values(planDetails.monthly_plans || {})[0] || 
                        Object.values(planDetails.annual_plans || {})[0];
      
      if (firstPlan) {
        const paypalPlan = firstPlan.paypal_plan || {};
        const paymentPrefs = paypalPlan.payment_preferences || firstPlan.payment_preferences || {};
        const taxes = paypalPlan.taxes || (firstPlan.tax_percentage ? {
          percentage: firstPlan.tax_percentage.toString(),
          inclusive: firstPlan.tax_inclusive
        } : {});
        
        // Extract base name (remove tier and billing cycle)
        const baseName = planDetails.name || '';
        
        setFormData({
          name: baseName,
          description: firstPlan.description || '',
          payment_preferences: {
            auto_bill_outstanding: paymentPrefs.auto_bill_outstanding !== undefined ? paymentPrefs.auto_bill_outstanding : true,
            setup_fee: paymentPrefs.setup_fee || { 
              value: firstPlan.setup_fee?.toString() || '', 
              currency_code: firstPlan.currency_code || 'USD' 
            },
            setup_fee_failure_action: paymentPrefs.setup_fee_failure_action || 'CONTINUE',
            payment_failure_threshold: paymentPrefs.payment_failure_threshold || 1
          },
          taxes: {
            percentage: taxes.percentage || firstPlan.tax_percentage?.toString() || ''
          }
        });
      }
    }
  }, [planDetails]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updateData: any = {};
    
    if (formData.name) updateData.name = formData.name;
    if (formData.description) updateData.description = formData.description;
    
    // Build payment preferences
    const paymentPrefs: any = {};
    if (formData.payment_preferences.auto_bill_outstanding !== undefined) {
      paymentPrefs.auto_bill_outstanding = formData.payment_preferences.auto_bill_outstanding;
    }
    if (formData.payment_preferences.setup_fee.value) {
      paymentPrefs.setup_fee = {
        value: formData.payment_preferences.setup_fee.value,
        currency_code: formData.payment_preferences.setup_fee.currency_code
      };
    }
    if (formData.payment_preferences.setup_fee_failure_action) {
      paymentPrefs.setup_fee_failure_action = formData.payment_preferences.setup_fee_failure_action;
    }
    if (formData.payment_preferences.payment_failure_threshold !== undefined) {
      paymentPrefs.payment_failure_threshold = formData.payment_preferences.payment_failure_threshold;
    }
    if (Object.keys(paymentPrefs).length > 0) {
      updateData.payment_preferences = paymentPrefs;
    }
    
    // Build taxes
    if (formData.taxes.percentage) {
      updateData.taxes = {
        percentage: formData.taxes.percentage
      };
    }
    
    onSave(updateData);
  };

  if (!planDetails) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Base Plan Information</DialogTitle>
          <DialogDescription>
            Update base information for all plans in this unified plan group. This will update all 6 plans (3 monthly + 3 annual tiers) via PayPal PATCH requests.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="base-name">Base Plan Name</Label>
              <Input
                id="base-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter base plan name"
                maxLength={127}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tier-specific names will be generated as: "{formData.name || 'Base Plan'} - Starter Monthly", etc.
              </p>
            </div>
            
            <div>
              <Label htmlFor="base-description">Description</Label>
              <Textarea
                id="base-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter plan description"
                maxLength={127}
                rows={3}
              />
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">Payment Preferences</Label>
            
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="base-auto-bill">Auto Bill Outstanding</Label>
                <p className="text-sm text-muted-foreground">Automatically bill outstanding amounts</p>
              </div>
              <Switch
                id="base-auto-bill"
                checked={formData.payment_preferences.auto_bill_outstanding}
                onCheckedChange={(checked) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    auto_bill_outstanding: checked
                  }
                })}
              />
            </div>

            <div>
              <Label htmlFor="base-setup-fee">Setup Fee</Label>
              <Input
                id="base-setup-fee"
                type="number"
                step="0.01"
                value={formData.payment_preferences.setup_fee.value}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    setup_fee: {
                      ...formData.payment_preferences.setup_fee,
                      value: e.target.value
                    }
                  }
                })}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label htmlFor="base-failure-action">Setup Fee Failure Action</Label>
              <select
                id="base-failure-action"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                value={formData.payment_preferences.setup_fee_failure_action}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    setup_fee_failure_action: e.target.value
                  }
                })}
              >
                <option value="CONTINUE">Continue</option>
                <option value="CANCEL">Cancel</option>
              </select>
            </div>

            <div>
              <Label htmlFor="base-failure-threshold">Payment Failure Threshold</Label>
              <Input
                id="base-failure-threshold"
                type="number"
                min="1"
                value={formData.payment_preferences.payment_failure_threshold}
                onChange={(e) => setFormData({
                  ...formData,
                  payment_preferences: {
                    ...formData.payment_preferences,
                    payment_failure_threshold: parseInt(e.target.value) || 1
                  }
                })}
                placeholder="1"
              />
            </div>
          </div>

          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">Tax Information</Label>
            
            <div>
              <Label htmlFor="base-tax-percentage">Tax Percentage</Label>
              <Input
                id="base-tax-percentage"
                type="text"
                value={formData.taxes.percentage}
                onChange={(e) => setFormData({
                  ...formData,
                  taxes: {
                    ...formData.taxes,
                    percentage: e.target.value
                  }
                })}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="border-t pt-4 flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Updating...' : 'Update Base Information'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface CreateAnnualPlanFormProps {
  monthlyPlan: any;
  planName?: string;
  onCreate: (planData: any) => void;
  processing: boolean;
}

const CreateAnnualPlanForm: React.FC<CreateAnnualPlanFormProps> = ({ 
  monthlyPlan, 
  planName,
  onCreate, 
  processing 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    currency_code: 'USD',
    tiers: [] as Array<{
      tier_name: string;
      starting_quantity: string;
      ending_quantity?: string;
      amount: number;
      currency_code: string;
      monthlyPrice?: number; // Store monthly price for comparison
    }>,
    has_setup_fee: false,
    setup_fee: 0,
    setup_fee_failure_action: 'CONTINUE',
    has_trial: false,
    trial_periods: [] as Array<{
      price: number;
      interval_unit: string;
      interval_count: number;
      cycles: number;
    }>,
    unlimited_cycles: true,
    total_cycles: 0,
        tax_calculation: 'no_tax' as 'no_tax' | 'add_tax' | 'include_tax',
    tax_percentage: '',
    payment_failure_threshold: 1,
    auto_bill_outstanding: true,
    interval_count: 1
  });

  useEffect(() => {
    if (monthlyPlan) {
      // Pre-fill form with monthly plan data, adjusted for annual
      const monthlyTiers = monthlyPlan.tiers || [];
      const paypalPlan = monthlyPlan.paypal_plan || {};
      const paymentPrefs = paypalPlan.payment_preferences || monthlyPlan.payment_preferences || {};
      const taxes = paypalPlan.taxes || (monthlyPlan.tax_percentage ? {
        percentage: monthlyPlan.tax_percentage.toString(),
        inclusive: monthlyPlan.tax_inclusive
      } : {});

      // Convert monthly prices to annual (default to 10x monthly)
      const annualTiers = monthlyTiers.map((tier: any) => {
        const monthlyPrice = parseFloat(tier.amount || tier.base_price || 0);
        return {
          tier_name: tier.tier_name,
          starting_quantity: tier.starting_quantity,
          ending_quantity: tier.ending_quantity,
          amount: monthlyPrice * 10, // Default to 10x monthly (annual discount)
          currency_code: tier.currency_code || 'USD',
          monthlyPrice: monthlyPrice // Store for comparison
        };
      });

      setFormData({
        name: planName ? `${planName} Annual` : `${monthlyPlan.paypal_name || monthlyPlan.paypal_plan_name || ''} Annual`,
        description: monthlyPlan.description || monthlyPlan.paypal_description || '',
        currency_code: monthlyPlan.paypal_currency_code || 'USD',
        tiers: annualTiers,
        has_setup_fee: !!monthlyPlan.setup_fee || !!paymentPrefs.setup_fee,
        setup_fee: monthlyPlan.setup_fee || (paymentPrefs.setup_fee?.value ? parseFloat(paymentPrefs.setup_fee.value) : 0),
        setup_fee_failure_action: paymentPrefs.setup_fee_failure_action || 'CONTINUE',
        has_trial: false, // Start with no trial, user can add
        trial_periods: [],
        unlimited_cycles: true,
        total_cycles: 0,
        tax_calculation: taxes.inclusive ? 'include_tax' : (taxes.percentage ? 'add_tax' : 'no_tax'),
        tax_percentage: taxes.percentage || monthlyPlan.tax_percentage?.toString() || '',
        payment_failure_threshold: paymentPrefs.payment_failure_threshold || 1,
        auto_bill_outstanding: paymentPrefs.auto_bill_outstanding !== undefined ? paymentPrefs.auto_bill_outstanding : true,
        interval_count: 1
      });
    }
  }, [monthlyPlan, planName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const planData: any = {
      name: formData.name,
      description: formData.description,
      currency_code: formData.currency_code,
      billing_cycle: 'annual',
      interval_count: formData.interval_count,
      tiers: formData.tiers.map((tier, index) => {
        const isLastTier = index === formData.tiers.length - 1;
        // Calculate base_price: use 12x monthly as base, current amount as potentially discounted
        const monthlyPrice = tier.monthlyPrice || 0;
        const basePrice = monthlyPrice * 12; // 12x monthly is the base (no discount)
        const currentAmount = tier.amount;
        const discountedPrice = currentAmount < basePrice ? currentAmount : null; // Only set if less than base
        
        const tierData: any = {
          tier_name: tier.tier_name,
          starting_quantity: tier.starting_quantity,
          base_price: basePrice, // Always use 12x monthly as base
          discounted_price: discountedPrice, // Set if current amount is less than base
          currency_code: tier.currency_code
        };
        if (!isLastTier && tier.ending_quantity) {
          tierData.ending_quantity = tier.ending_quantity;
        }
        return tierData;
      }),
      unlimited_cycles: formData.unlimited_cycles,
      total_cycles: formData.unlimited_cycles ? 0 : formData.total_cycles,
      tax_calculation: formData.tax_calculation,
      tax_percentage: formData.tax_calculation !== 'no_tax' && formData.tax_percentage ? parseFloat(formData.tax_percentage) : undefined,
      payment_failure_threshold: formData.payment_failure_threshold,
      auto_bill_outstanding: formData.auto_bill_outstanding,
      has_setup_fee: formData.has_setup_fee,
      setup_fee: formData.has_setup_fee ? formData.setup_fee : undefined,
      setup_fee_failure_action: formData.has_setup_fee ? formData.setup_fee_failure_action : undefined,
      has_trial: formData.has_trial,
      trial_periods: formData.has_trial ? formData.trial_periods : undefined,
      subscription_tier: monthlyPlan?.tier || 'starter'
    };

    onCreate(planData);
  };

  const addTier = () => {
    const lastTier = formData.tiers[formData.tiers.length - 1];
    const nextStarting = lastTier ? (parseInt(lastTier.ending_quantity || lastTier.starting_quantity) + 1) : 1;
    
    setFormData({
      ...formData,
      tiers: [
        ...formData.tiers,
        {
          tier_name: `Tier ${formData.tiers.length + 1}`,
          starting_quantity: nextStarting.toString(),
          ending_quantity: undefined,
          amount: 0,
          currency_code: formData.currency_code
        }
      ]
    });
  };

  const removeTier = (index: number) => {
    setFormData({
      ...formData,
      tiers: formData.tiers.filter((_, i) => i !== index)
    });
  };

  if (!monthlyPlan) {
    return null;
  }

  // Calculate comparison prices for each tier
  const getComparisonPrices = (tier: typeof formData.tiers[0]) => {
    const monthlyPrice = tier.monthlyPrice || 0;
    const price10x = monthlyPrice * 10;
    const price12x = monthlyPrice * 12;
    const discount10x = ((price12x - price10x) / price12x) * 100;
    const discountCurrent = tier.amount < price12x ? ((price12x - tier.amount) / price12x) * 100 : 0;
    
    return { price10x, price12x, discount10x, discountCurrent };
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Annual Plan</CardTitle>
        <CardDescription>
          Create an annual plan based on the monthly plan. Prices are pre-filled as 10x monthly prices (offering a discount), but you can adjust them.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="annual_name">Plan Name</Label>
              <Input
                id="annual_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                maxLength={127}
              />
            </div>
            <div>
              <Label htmlFor="annual_currency">Currency</Label>
              <Select
                value={formData.currency_code}
                onValueChange={(value) => setFormData({ ...formData, currency_code: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="annual_description">Description <span className="text-red-500">*</span></Label>
            <Textarea
              id="annual_description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              maxLength={127}
              minLength={1}
              required
              rows={2}
              placeholder="Enter plan description (required)"
            />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-base font-semibold">Pricing Tiers</Label>
              <Button type="button" variant="outline" size="sm" onClick={addTier}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tier
              </Button>
            </div>
            <div className="space-y-4">
              {formData.tiers.map((tier, index) => {
                const comparison = getComparisonPrices(tier);
                return (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="space-y-4">
                        <div className="grid grid-cols-12 gap-4 items-end">
                          <div className="col-span-3">
                            <Label>Tier Name</Label>
                            <Input
                              value={tier.tier_name}
                              onChange={(e) => {
                                const newTiers = [...formData.tiers];
                                newTiers[index].tier_name = e.target.value;
                                setFormData({ ...formData, tiers: newTiers });
                              }}
                              required
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>From</Label>
                            <Input
                              type="number"
                              value={tier.starting_quantity}
                              onChange={(e) => {
                                const newTiers = [...formData.tiers];
                                newTiers[index].starting_quantity = e.target.value;
                                setFormData({ ...formData, tiers: newTiers });
                              }}
                              required
                              min="1"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label>To</Label>
                            <Input
                              type="number"
                              value={tier.ending_quantity || ''}
                              onChange={(e) => {
                                const newTiers = [...formData.tiers];
                                newTiers[index].ending_quantity = e.target.value || undefined;
                                setFormData({ ...formData, tiers: newTiers });
                              }}
                              placeholder="∞"
                              min="1"
                            />
                          </div>
                          <div className="col-span-3">
                            <Label>Price ({formData.currency_code})</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={tier.amount}
                              onChange={(e) => {
                                const newTiers = [...formData.tiers];
                                newTiers[index].amount = parseFloat(e.target.value) || 0;
                                setFormData({ ...formData, tiers: newTiers });
                              }}
                              required
                              min="0"
                            />
                          </div>
                          <div className="col-span-2">
                            {formData.tiers.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTier(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {/* Price Comparison */}
                        <div className="bg-muted/50 p-3 rounded-md space-y-1 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Monthly Price:</span>
                            <span className="font-medium">{formData.currency_code} {tier.monthlyPrice?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">10x Monthly (Default):</span>
                            <span className="font-medium">{formData.currency_code} {comparison.price10x.toFixed(2)} <span className="text-green-600">({comparison.discount10x.toFixed(1)}% off)</span></span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">12x Monthly (No Discount):</span>
                            <span className="font-medium">{formData.currency_code} {comparison.price12x.toFixed(2)}</span>
                          </div>
                          {comparison.discountCurrent > 0 && (
                            <div className="flex items-center justify-between pt-1 border-t">
                              <span className="text-muted-foreground">Current Price Discount:</span>
                              <span className="font-medium text-green-600">{comparison.discountCurrent.toFixed(1)}% off</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <Label>Billing Frequency</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={formData.interval_count}
                  onChange={(e) => setFormData({ ...formData, interval_count: parseInt(e.target.value) || 1 })}
                  min="1"
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">Year(s)</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Unlimited Billing Cycles</Label>
                <p className="text-sm text-muted-foreground">Bill indefinitely or set a limit</p>
              </div>
              <Switch
                checked={formData.unlimited_cycles}
                onCheckedChange={(checked) => setFormData({ ...formData, unlimited_cycles: checked })}
              />
            </div>
            {!formData.unlimited_cycles && (
              <div>
                <Label>Total Billing Cycles</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.total_cycles}
                  onChange={(e) => setFormData({ ...formData, total_cycles: parseInt(e.target.value) || 0 })}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto Bill Outstanding</Label>
                <p className="text-sm text-muted-foreground">Automatically bill outstanding amounts</p>
              </div>
              <Switch
                checked={formData.auto_bill_outstanding}
                onCheckedChange={(checked) => setFormData({ ...formData, auto_bill_outstanding: checked })}
              />
            </div>
            <div>
              <Label>Payment Failure Threshold</Label>
              <Input
                type="number"
                min="1"
                value={formData.payment_failure_threshold}
                onChange={(e) => setFormData({ ...formData, payment_failure_threshold: parseInt(e.target.value) || 1 })}
              />
              <p className="text-sm text-muted-foreground mt-1">Missed cycles before pause</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Setup Fee</Label>
                <p className="text-sm text-muted-foreground">Charge a one-time setup fee</p>
              </div>
              <Switch
                checked={formData.has_setup_fee}
                onCheckedChange={(checked) => setFormData({ ...formData, has_setup_fee: checked })}
              />
            </div>
            {formData.has_setup_fee && (
              <>
                <div>
                  <Label>Setup Fee Amount ({formData.currency_code})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.setup_fee}
                    onChange={(e) => setFormData({ ...formData, setup_fee: parseFloat(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
                <div>
                  <Label>Setup Fee Failure Action</Label>
                  <Select
                    value={formData.setup_fee_failure_action}
                    onValueChange={(value) => setFormData({ ...formData, setup_fee_failure_action: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CONTINUE">Continue</SelectItem>
                      <SelectItem value="CANCEL">Cancel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <Label>Tax Calculation</Label>
              <Select
                value={formData.tax_calculation}
                onValueChange={(value: 'no_tax' | 'add_tax' | 'include_tax') => setFormData({ ...formData, tax_calculation: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_tax">Don't calculate tax</SelectItem>
                  <SelectItem value="add_tax">Add tax on top</SelectItem>
                  <SelectItem value="include_tax">Include tax in price</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.tax_calculation !== 'no_tax' && (
              <div>
                <Label>Tax Percentage</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.tax_percentage}
                  onChange={(e) => setFormData({ ...formData, tax_percentage: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
            )}
          </div>

          <div className="border-t pt-4 flex justify-end gap-4">
            <Button type="submit" disabled={processing || formData.tiers.length === 0}>
              {processing ? 'Creating...' : 'Create Annual Plan'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default PlanDetails;

